// lib/note-summary.js — NOTE SUMMARY CLIENT HELPER
// ═══════════════════════════════════════════════════════════
// Client-side wrapper that asks the chat API to generate a
// Haiku-powered context summary for a freshly-saved note, then
// PATCHes the result onto the note row.
//
// Fire-and-forget from hooks/use-notes.js after createNote — the
// card shows the sentence-slicer excerpt first, then re-renders
// with the quality summary when it lands (~300ms later).
//
// Only runs for note types that benefit: voice (always) and typed
// notes over a length threshold. Ideas and highlights skip because
// they're already short and self-explanatory.
// ═══════════════════════════════════════════════════════════

const LONG_NOTE_THRESHOLD = 150; // chars — below this, typed notes skip Haiku

export function shouldGenerateSummary(note) {
  if (!note?.content) return false;
  if (note.note_type === 'voice') return true;
  if (note.note_type === 'note' && note.content.length >= LONG_NOTE_THRESHOLD) return true;
  return false;
}

// Fire-and-forget. Returns a promise that resolves to the updated
// note (or null if the summary couldn't be generated / saved).
export async function generateAndAttachSummary(note, { supabase, onUpdated }) {
  try {
    const headers = await authHeaders(supabase);
    const genRes = await fetch('/api/chat', {
      method: 'POST',
      headers,
      body: JSON.stringify({
        action: 'generateNoteDescription',
        noteType: note.note_type,
        content: note.content,
      }),
    });
    if (!genRes.ok) return null;
    const { description, bullets } = await genRes.json();
    if (!description && (!bullets || bullets.length === 0)) return null;

    const patchRes = await fetch('/api/user/notes', {
      method: 'PATCH',
      headers,
      body: JSON.stringify({
        id: note.id,
        description: description || null,
        summary_bullets: Array.isArray(bullets) ? bullets : [],
      }),
    });
    if (!patchRes.ok) return null;
    const { note: updated } = await patchRes.json();
    onUpdated?.(updated);
    return updated;
  } catch (e) {
    console.error('generateAndAttachSummary:', e);
    return null;
  }
}

async function authHeaders(supabase) {
  const headers = { 'Content-Type': 'application/json' };
  try {
    const { data: { session } } = await supabase.auth.getSession();
    if (session?.access_token) headers.Authorization = `Bearer ${session.access_token}`;
  } catch { /* non-fatal */ }
  return headers;
}
